
import { useState, useMemo, useCallback, useEffect } from 'react';
import _ from 'underscore';

import {
  CSMLAdType,
  CSMLAssetSource,
  getPreviewText,
  getSizeErrorRatioList,
  getAllSizeErrorRatioList,
  getPlacementsToExpand,
} from '@bingads-webui-campaign-react/ads-studio-app-shared-components';
import { ImageExperienceImage } from '@bingads-webui-campaign-react/image-experience-model';
import {
  useAdPreviewModel,
  UAPPlacementTypeAlias,
  getCropSettingsFromRoi,
} from '@bingads-webui-campaign-react/unified-ad-preview-library-cmui';
import { usePageActiveTimeTracker } from '@bingads-webui-campaign-react/common-hooks';

import GenericImage from '../media/1.91to1.jpg';

const getPlacementSetConfig = (image, getSmartCroppings, isAudienceAdSmartCropEnabled, isMMAV2Enabled, isPMaxAspectRatiosV2Enabled, isAdPreviewRefreshEnabled, getExpandedCroppingsFunc = undefined) => ([
  {
    image,
    placementSets: [
      {
        placementSetName: CSMLAdType.PMax,
        // eslint-disable-next-line no-nested-ternary
        placementTypes: isPMaxAspectRatiosV2Enabled ? (isAdPreviewRefreshEnabled ? UAPPlacementTypeAlias.PMaxV2.AllV2 : UAPPlacementTypeAlias.PMaxV2.All) : UAPPlacementTypeAlias.PMax.All,
        getDefaultPlacementsCropping: getSmartCroppings,
      },
      {
        placementSetName: CSMLAdType.MMA,
        placementTypes: UAPPlacementTypeAlias.MMA.All,
        getDefaultPlacementsCropping: getSmartCroppings,
      },
      {
        placementSetName: CSMLAdType.Audience,
        placementTypes: isMMAV2Enabled ? UAPPlacementTypeAlias.MSANV2.All : UAPPlacementTypeAlias.MSAN.All,
        // eslint-disable-next-line no-nested-ternary
        getDefaultPlacementsCropping: getExpandedCroppingsFunc ? getExpandedCroppingsFunc(CSMLAdType.Audience) : (isAudienceAdSmartCropEnabled ? getSmartCroppings : undefined),
      },
      {
        placementSetName: CSMLAdType.ImageExtension,
        placementTypes: isAdPreviewRefreshEnabled ? UAPPlacementTypeAlias.ImageExt.AllV2 : UAPPlacementTypeAlias.ImageExt.All,
        getDefaultPlacementsCropping: isAudienceAdSmartCropEnabled ? getSmartCroppings : undefined,
      },
    ],
  }, {
    image: GenericImage,
    placementSets: [
      {
        placementSetName: CSMLAdType.ImageExtension,
        placementTypes: [
          UAPPlacementTypeAlias.ImageExt.BingDesktopMultiImage,
          UAPPlacementTypeAlias.ImageExt.BingMobileMultiImage,
        ],
      },
    ],
  }]);

export const useAdPlacement = ({
  image,
  permissions,
  setDataLoaded,
  logger,
  i18n,
  getSmartCroppings,
  setLoadingStatus,
  imageCroppingTasks,
  isImageFittingEnabled,
  dataService,
}) => {
  const isAudienceAdSmartCropEnabled = permissions && permissions.dynamic.AudienceAdSmartCrop;
  const [adType, setAdType] = useState(CSMLAdType.PMax);
  const [errorTip, setErrorTip] = useState('');
  const [showRequiredOnly, setShowRequiredOnly] = useState(false);
  const [currentImage, setCurrentImage] = useState(null);

  const [editingPlacementInfo, setEditingPlacementInfo] = useState(null);

  // useClarityTags([ClarityTags.LaunchedAssetLibrary, ClarityTags.LaunchedEditImage]);

  // click on a placement's edit aspect ratio button
  const onEditPlacement = useCallback((placementInfo) => {
    setEditingPlacementInfo(placementInfo);
    logger.logTrace('Edit Page: onEditPlacement', { imageId: currentImage.id });
  }, [currentImage, logger]);

  const onAdPreviewError = useCallback((err) => {
    logger.logError(`Edit Page: ad preview error, ${JSON.stringify(err)}`);
  }, [logger]);

  const {
    firstImage,
    setPlacementConfig,
    multiImagePlacementSets: placementSets,
    updatePlacementCropping,
    updatePlacementCroppingByImageType,
    hasDataChanged: hasCropSettingsChanged,
    changedCropSettings,
  } = useAdPreviewModel(null, onAdPreviewError);
  const { imageMeta: imageInfo, imageCropping: cropSettings } = firstImage || {};

  const placementList = useMemo(() => {
    const { placementList: pl } = _.find(placementSets, ({ placementSetName }) => placementSetName === adType) || {};
    return pl;
  }, [adType, placementSets]);

  const previewTexts = useMemo(
    () => getPreviewText(adType === CSMLAdType.ImageExtension, i18n),
    [adType, i18n]
  );

  usePageActiveTimeTracker(logger, 'Edit image');

  useEffect(() => {
    if (image && image.url) {
      if (setDataLoaded) {
        setDataLoaded();
      }
      setLoadingStatus(true);
      setCurrentImage(prev => (prev && prev.url === image.url ? prev : image));
      logger.logTrace('Edit Page: page loaded', { imageId: image.id });
    }
  }, [image, logger, setLoadingStatus]);

  const isMMAV2Enabled = useMemo(() => permissions && permissions.IsMMAV2Enabled && !(permissions.IsAmplifyEnabled && permissions.dynamic.DisplayPlusAmplify), [permissions]);
  const isPMaxAspectRatiosV2Enabled = useMemo(() => permissions && permissions.dynamic.PMaxNewAspectRatios && !permissions.dynamic.PMaxExcludeNewAspectRatios, [permissions]);
  const isAdPreviewRefreshEnabled = useMemo(() => _.get(permissions, ['dynamic', 'AdPreviewRefresh']), [permissions]);

  const { imageFit } = dataService;

  const getExpandedCroppingsInner = useCallback(adTypeInner => (argObj) => {
    const { imageMeta } = argObj;
    // we should call fitting if
    // 1. the image is applied filter/blur/background change, and not saved to library yet
    // 2. the image is stock image from search result page which is not saved to library yet
    setLoadingStatus(true);
    const placementsToExpand = getPlacementsToExpand(adTypeInner);
    return imageFit({
      imageUrl: currentImage.url,
      placements: placementsToExpand,
      skipOutpainting: currentImage.sourceType === CSMLAssetSource.Shutterstock,
    }).then(res => getCropSettingsFromRoi({
      ...argObj,
      imageMeta: {
        ...imageMeta,
        expandedMeta: res,
      },
    })).catch(() => {
    }).finally(() => {
      setLoadingStatus(false);
    });
  }, [currentImage, imageFit, setLoadingStatus]);
  useEffect(() => {
    let getExpandedCroppingsFunc;
    if (isImageFittingEnabled && currentImage && currentImage.roiInfo && currentImage.id) {
      getExpandedCroppingsFunc = () => getCropSettingsFromRoi;
    }
    if (isImageFittingEnabled && currentImage && !currentImage.id && !currentImage.roiInfo) {
      getExpandedCroppingsFunc = getExpandedCroppingsInner;
    }

    if (currentImage && currentImage.originalUrl) {
      const placementSetConfig = getPlacementSetConfig(currentImage, getSmartCroppings, isAudienceAdSmartCropEnabled, isMMAV2Enabled, isPMaxAspectRatiosV2Enabled, isAdPreviewRefreshEnabled, getExpandedCroppingsFunc);
      setPlacementConfig(placementSetConfig);
    } else if (currentImage && currentImage.url) {
      const clonedImage = { ...currentImage, originalUrl: currentImage.url };
      const placementSetConfig = getPlacementSetConfig(clonedImage, getSmartCroppings, isAudienceAdSmartCropEnabled, isMMAV2Enabled, isPMaxAspectRatiosV2Enabled, isAdPreviewRefreshEnabled, getExpandedCroppingsFunc);
      setPlacementConfig(placementSetConfig);
    }
  }, [setPlacementConfig, isAudienceAdSmartCropEnabled, currentImage, getSmartCroppings, isMMAV2Enabled, isImageFittingEnabled, getExpandedCroppingsInner, isPMaxAspectRatiosV2Enabled, isAdPreviewRefreshEnabled]);

  useEffect(() => {
    if (placementList && placementList.length) {
      setLoadingStatus(false);
    }
  }, [setLoadingStatus, placementList]);

  const maxCropBox = useMemo(() => (imageInfo ? {
    SourceX: 0,
    SourceY: 0,
    SourceWidth: imageInfo.originalWidth,
    SourceHeight: imageInfo.originalHeight,
  } : null), [imageInfo]);

  const handleInvalidCropSetting = useCallback((invalidRatioList) => {
    let errorInfo = '';
    if (invalidRatioList.length === 1) {
      errorInfo = i18n.getString(
        _TL_('Your image doesn\'t meet the size requirements for the following ad placement: {{ratio}} {{placement}}'),
        {
          ratio: `<b>${invalidRatioList[0].aspectRatioAsRatio}</b>`,
          placement: `<b>${i18n.getString(invalidRatioList[0].previewTypeString)}</b>`,
        }
      );
    } else if (invalidRatioList.length === 2) {
      errorInfo = i18n.getString(
        _TL_('Your image doesn\'t meet the size requirements for the following ad placements: {{ratio1}} {{placement1}} and {{ratio2}} {{placement2}}'),
        {
          ratio1: `<b>${invalidRatioList[0].aspectRatioAsRatio}</b>`,
          ratio2: `<b>${invalidRatioList[1].aspectRatioAsRatio}</b>`,
          placement1: `<b>${i18n.getString(invalidRatioList[0].previewTypeString)}</b>`,
          placement2: `<b>${i18n.getString(invalidRatioList[1].previewTypeString)}</b>`,
        }
      );
    }

    setErrorTip(errorInfo);
  }, [i18n]);

  useEffect(() => {
    const invalidRatioList = getSizeErrorRatioList(
      adType,
      cropSettings || maxCropBox,
      true,
      isMMAV2Enabled,
      isPMaxAspectRatiosV2Enabled
    );
    handleInvalidCropSetting(invalidRatioList);
  }, [adType, cropSettings, handleInvalidCropSetting, isMMAV2Enabled, isPMaxAspectRatiosV2Enabled, maxCropBox]);

  const saveCropPlacement = useCallback((placement, newCropping) => {
    const { placementMeta } = placement || {};
    const { placementType, imageType } = placementMeta || {};
    if (adType === CSMLAdType.ImageExtension || adType === CSMLAdType.PMax) {
      // for image extension placements, all placements with same image type should be updated together
      updatePlacementCroppingByImageType({ placementSetName: adType, imageType, newPlacementCropping: newCropping });
    } else {
      updatePlacementCropping({ placementSetName: adType, placementType, newPlacementCropping: newCropping });
    }
    setEditingPlacementInfo(null);
    logger.logTrace('Edit page: save crop placement', {
      imageId: currentImage.id,
      adType,
      placementType: imageType,
      cropSettings: JSON.stringify(newCropping),
    });
  }, [adType, currentImage, logger, updatePlacementCropping, updatePlacementCroppingByImageType]);

  const cancelCropPlacement = useCallback(() => {
    setEditingPlacementInfo(null);
    logger.logTrace('Edit page: cancel edit placement', { imageId: currentImage.id });
  }, [currentImage, logger]);

  const switchAdType = useCallback((type) => {
    logger.logTrace('Edit Page: switchAdType', { imageId: currentImage.id, adType: type });
    setAdType(type);
  }, [currentImage, logger]);

  const switchShowRequiredOnly = useCallback((checked) => {
    setShowRequiredOnly(checked);
    logger.logTrace('Edit page: show required only', { imageId: currentImage.id, showRequiredOnly: checked });
  }, [currentImage, logger]);

  const updateImage = useCallback((newImage) => {
    if (!_.isEqual(newImage, currentImage)) {
      setCurrentImage(newImage);
    }
  }, [currentImage]);

  const previewImage = useMemo(() => (imageInfo ?
    new ImageExperienceImage({
      src: imageInfo.originalUrl,
      width: imageInfo.originalWidth,
      height: imageInfo.originalHeight,
    })
    : null), [imageInfo]);

  const hasCroppingTask = useMemo(() => {
    if (currentImage && currentImage.id) {
      const onGoingTask = imageCroppingTasks && imageCroppingTasks[currentImage.id];

      return !!onGoingTask;
    }
    return false;
  }, [currentImage, imageCroppingTasks]);

  const cropSettingsToUpdate = useMemo(() => _.first(changedCropSettings), [changedCropSettings]);

  const allSizeInvalidRatioList = useMemo(() => getAllSizeErrorRatioList(cropSettings || maxCropBox, false, isMMAV2Enabled), [cropSettings, isMMAV2Enabled, maxCropBox]);

  const requiredSizeInvalidRatioList = useMemo(() => getAllSizeErrorRatioList(cropSettings || maxCropBox, true, isMMAV2Enabled), [cropSettings, isMMAV2Enabled, maxCropBox]);

  const allViewErrorTip = useMemo(() => {
    let errorInfo = '';
    if (requiredSizeInvalidRatioList.length === 1) {
      errorInfo = i18n.getString(
        _TL_('Your image doesn\'t meet the size requirements for {{ratio}} {{placement}} ads.'),
        {
          ratio: requiredSizeInvalidRatioList[0].aspectRatioAsRatio,
          placement: i18n.getString(requiredSizeInvalidRatioList[0].previewTypeString),
        }
      );
    } else if (requiredSizeInvalidRatioList.length === 2) {
      errorInfo = i18n.getString(
        _TL_('Your image doesn\'t meet the size requirements for {{ratio1}} {{previewName1}} and {{ratio2}} {{previewName2}} ads.'),
        {
          ratio1: requiredSizeInvalidRatioList[0].aspectRatioAsRatio,
          ratio2: requiredSizeInvalidRatioList[1].aspectRatioAsRatio,
          previewName1: i18n.getString(requiredSizeInvalidRatioList[0].previewTypeString),
          previewName2: i18n.getString(requiredSizeInvalidRatioList[1].previewTypeString),
        }
      );
    }

    return errorInfo;
  }, [i18n, requiredSizeInvalidRatioList]);

  return {
    adType,
    currentViewErrorTip: errorTip,
    allViewErrorTip,
    showRequiredOnly,
    editingPlacementInfo,
    placementList,
    firstImage,
    previewTexts,
    maxCropBox,
    previewImage,
    hasCroppingTask,
    cropSettingsToUpdate,
    hasCropSettingsChanged,
    onEditPlacement,
    saveCropPlacement,
    cancelCropPlacement,
    switchAdType,
    switchShowRequiredOnly,
    updateImage,
    allSizeInvalidRatioList,
    requiredSizeInvalidRatioList,
  };
};
